// src/components/Navbar.jsx
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import "./css/Navbar.css";
import { normalizeHandle, ensureAt } from "../utils/handle";
import { useTheme } from "../context/ThemeContext";
import MeModal from "../components/MeModal";
import { getFirestore, doc, getDoc, runTransaction, serverTimestamp, collection, query, where, getDocs, documentId, updateDoc, writeBatch } from 'firebase/firestore';
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp } from "../firebase";
import useMessages from "../hooks/useMessages";
import useNotifications from "../hooks/useNotifications";
import { markNotificationRead, markMultipleRead } from "../libs/notificationsClient";
import MessagesInboxModal from "../components/MessagesInboxModal";
import { formatDistanceToNow } from "date-fns";

async function testCallable() {
  try {
    const auth = getAuth(firebaseApp);
    if (!auth || !auth.currentUser) {
      console.log("testCallable: no signed-in user");
      return;
    }
    // force-refresh token to be safe
    await auth.currentUser.getIdToken(true);
    const functions = getFunctions(firebaseApp, "us-central1");
    const fn = httpsCallable(functions, "markNotificationRead");
    const res = await fn({ notificationId: "5PePH4N2N4xqRlhF9JAK" });
    console.log("testCallable result:", res);
  } catch (err) {
    console.error("testCallable error:", err && err.code, err && err.message);
  }
}
window.testCallable = testCallable; // expose to console

export default function Navbar({ onOpenAuth, onAddTrip, onViewTrips, onViewProfile, onNavigateHome }) {
  const { currentUser, profile, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const menuRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const msgRef = useRef(null);
  const [localProfile, setLocalProfile] = useState(profile);
  const [localNotifications, setLocalNotifications] = useState([]);

  const [notifSenders, setNotifSenders] = useState({});

  useEffect(() => {
    let mounted = true;
    const db = getFirestore();

    async function loadSendersForNotifications() {
      const notifs = Array.isArray(localNotifications) ? localNotifications : [];
      const uids = Array.from(new Set(notifs.map((n) => n.from).filter(Boolean)));
      if (!uids.length) {
        if (mounted) setNotifSenders({});
        return;
      }

      // Firestore 'in' supports max 10 elements; batch if needed
      const batches = [];
      for (let i = 0; i < uids.length; i += 10) {
        batches.push(uids.slice(i, i + 10));
      }

      const results = {};
      try {
        for (const batchUids of batches) {
          const q = query(
            collection(db, "users"),
            where(documentId(), "in", batchUids)
          );
          const snap = await getDocs(q);
          snap.forEach((d) => {
            const data = d.data() || {};
            results[d.id] = {
              handle: data.handle || null,
              displayName: data.displayName || null,
              photoURL: data.photoURL || data.photoPath || null,
            };
          });
        }
        if (mounted) setNotifSenders(results);
      } catch (err) {
        console.error("Failed to load notif senders:", err);
        // keep prior map if any
      }
    }

    loadSendersForNotifications();

    return () => {
      mounted = false;
    };
  }, [localNotifications]);


  useEffect(() => setLocalProfile(profile), [profile]);

  const { notifications, loading: notifLoading, unreadCount } = useNotifications({ limitResults: 20 });

  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [messagesModalPartner, setMessagesModalPartner] = useState(null);

  // Firebase helpers
  const db = getFirestore();
  const auth = getAuth();
  const currentUid = currentUser?.uid || null;

  // Messages hook
  const { conversations, loading: convLoading } = useMessages(
    db,
    auth,
    currentUid,
    { limitResults: 50, includeLastItem: true, includePartnerProfiles: true, hydrateItems: true }
  );

  const conversationsArray = Array.isArray(conversations) ? conversations : [];

  // Local copy of notifications (optimistic UI)
  useEffect(() => {
    setLocalNotifications(Array.isArray(notifications) ? notifications : []);
  }, [notifications]);

  // Unread count helper for messages
  function getUnreadCountForConv(conv, currentUid) {
    if (!conv) return 0;
    try {
      const uidStr = currentUid ? String(currentUid) : null;

      // 1) explicit per-user map: unreadCounts?.[uid] or unread_counts style
      const mapCount = (conv.unreadCounts && typeof conv.unreadCounts === "object") ? Number(conv.unreadCounts[uidStr] || 0) : 0;
      if (mapCount && mapCount > 0) return mapCount;

      // also tolerate singular key naming
      if (conv.unread && typeof conv.unread === "number") return conv.unread;
      if (typeof conv.unreadCount === "number") return conv.unreadCount;

      // 2) If conv.items exists (prefetched/hydrated), count items where to === currentUid and read !== true
      if (Array.isArray(conv.items) && conv.items.length > 0) {
        let c = 0;
        for (const it of conv.items) {
          if (!it || typeof it !== "object") continue;
          const isRead = it.read === true;
          // If item has 'to', only count items addressed to me
          if (it.to) {
            if (String(it.to) === uidStr && !isRead) c++;
          } else if (!isRead) {
            // no 'to' field — conservative: count as unread
            c++;
          }
        }
        if (c > 0) return c;
      }

      // 3) Fallback to lastMessage object
      const last = conv.lastMessage || conv.lastMessageObj || conv.lastItem || conv.last || null;
      if (last && typeof last === "object") {
        const isRead = last.read === true;
        if (last.to) {
          if (String(last.to) === uidStr && !isRead) return 1;
          return 0;
        }
        if (!isRead) return 1;
        return 0;
      }

      return 0;
    } catch (err) {
      console.error("[getUnreadCountForConv] error:", err, conv);
      return 0;
    }
  }

  const unreadMessagesCount = conversationsArray.reduce((acc, c) => acc + getUnreadCountForConv(c, currentUid), 0);

  // Click/outside handlers
  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (msgRef.current && !msgRef.current.contains(e.target)) setMsgOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setNotifOpen(false);
        setMsgOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleToggleMenu = (e) => { e?.preventDefault?.(); setOpen((s) => !s); };
  const handleToggleNotif = (e) => { e?.preventDefault?.(); setNotifOpen((s) => !s); setMsgOpen(false); };
  const handleToggleMsg = (e) => { e?.preventDefault?.(); setMsgOpen((s) => !s); setNotifOpen(false); };

  const handleAddTrip = () => {
    setOpen(false);
    if (!currentUser || !currentUser.uid) {
      if (typeof onOpenAuth === "function") onOpenAuth("login");
      else window.dispatchEvent(new CustomEvent("jift:openAuth", { detail: { section: "login" } }));
      return;
    }
    if (typeof onAddTrip === "function") onAddTrip();
    else {
      window.dispatchEvent(new CustomEvent("jift:openAddTrip"));
      console.warn("[Navbar] onAddTrip missing — dispatched fallback event jift:openAddTrip");
    }
  };

  const handleViewTrips = () => {
    setOpen(false);
    const rawHandle = localProfile?.handle || (currentUser?.email && currentUser.email.split("@")[0]);
    if (!rawHandle) {
      if (typeof onNavigateHome === "function") onNavigateHome();
      else window.location.href = `${window.location.origin}/`;
      return;
    }
    const handleWithAt = ensureAt(rawHandle);
    if (typeof onViewTrips === "function") { onViewTrips(handleWithAt); return; }
    try {
      const withoutAt = normalizeHandle(handleWithAt);
      const encoded = encodeURIComponent(withoutAt);
      window.location.href = `${window.location.origin}/@${encoded}`;
    } catch (err) { console.warn("[Navbar] fallback navigation failed", err); }
  };

  const openMessagesForPartner = (partnerUid) => {
    if (!partnerUid) return;
    setMsgOpen(false);
    setMessagesModalPartner(partnerUid);
    setShowMessagesModal(true);
  };
  const openAllMessages = () => {
    setMsgOpen(false);
    setMessagesModalPartner(null);
    setShowMessagesModal(true);
  };

  const handleLogout = async () => {
    setOpen(false);
    try { await logout(); } catch (err) { console.error("Logout failed", err); }
  };

  const displayName = localProfile?.handle ? `@${localProfile.handle}` : (currentUser?.email || "Guest");
  const handleOpenAuthClick = (mode = "login") => {
    if (typeof onOpenAuth === "function") onOpenAuth(mode);
    else window.dispatchEvent(new CustomEvent("jift:openAuth", { detail: { section: mode } }));
  };

  const handleLogoClick = (e) => {
    e?.preventDefault?.();
    if (typeof onNavigateHome === "function") { onNavigateHome(); return; }
    try { window.location.href = `${window.location.origin}/`; } catch (err) { console.error("[Navbar] navigation to home failed", err); }
  };

  // Profile save (unchanged)
  const handleSaveProfileToFirestore = async (updatedProfile) => {
    const authUid = currentUser?.uid || null;
    const uidFromProfile = profile?.uid || profile?.id || profile?.docId || null;
    const uid = authUid || uidFromProfile || updatedProfile.uid;
    if (!uid) throw new Error("Cannot determine user id. Please sign in and try again.");
    if (!currentUser || !currentUser.uid) throw new Error("You must be signed in to update your profile.");
    if (currentUser.uid !== uid) throw new Error("You are not authorized to edit this profile.");

    const db = getFirestore();
    const oldHandle = profile?.handle || "";
    const newHandle = updatedProfile.handle;
    if (!newHandle) throw new Error("Invalid handle.");

    try {
      await runTransaction(db, async (tx) => {
        const userRef = doc(db, "users", uid);
        const newHandleRef = doc(db, "handles", newHandle);
        const [newSnap] = await Promise.all([tx.get(newHandleRef), tx.get(userRef)]);

        let oldHandleRef = null;
        let oldSnap = null;
        if (oldHandle && oldHandle !== newHandle) {
          oldHandleRef = doc(db, "handles", oldHandle);
          oldSnap = await tx.get(oldHandleRef);
        }

        if (newHandle !== oldHandle) {
          if (newSnap.exists()) {
            const data = newSnap.data();
            if (!data || data.uid !== uid) throw new Error("Handle already taken. Choose another handle.");
          }
        }

        tx.set(userRef, {
          displayName: updatedProfile.displayName,
          handle: updatedProfile.handle,
          updatedAt: serverTimestamp(),
        }, { merge: true });

        if (newHandle !== oldHandle) {
          if (!newSnap.exists()) {
            tx.set(newHandleRef, { uid, createdAt: serverTimestamp() });
          }
        }
      });

      const propagated = {
        ...(localProfile || {}),
        displayName: updatedProfile.displayName,
        handle: updatedProfile.handle,
        updatedAt: new Date().toISOString(),
        uid,
      };

      setLocalProfile((p) => ({ ...p, ...propagated }));
      window.dispatchEvent(new CustomEvent("jift:profileUpdated", { detail: propagated }));
      return;
    } catch (err) {
      console.error("Profile save transaction failed:", err);
      if (err.message && /permission|unauth/i.test(err.message)) throw new Error("Permission denied. Make sure you are signed in as the profile owner.");
      throw new Error(err?.message || "Failed to save profile.");
    }
  };

  // ---------- Notifications (functions-only approach) ----------
  function optimisticMarkLocalRead(id) {
    setLocalNotifications((prev) =>
      Array.isArray(prev) ? prev.map((x) => (x.id === id ? { ...x, read: true } : x)) : prev
    );
  }
  function revertLocalRead(id) {
    setLocalNotifications((prev) =>
      Array.isArray(prev) ? prev.map((x) => (x.id === id ? { ...x, read: false } : x)) : prev
    );
  }
  /** Update a single notification's read flag (compliant with rules) */
  async function markNotificationReadDirectly(notificationId) {
    if (!notificationId) throw new Error("Missing notificationId");

    const db = getFirestore(firebaseApp);
    const authLocal = getAuth(firebaseApp);
    const user = authLocal.currentUser;

    if (!user) throw new Error("Not signed in");

    const ref = doc(db, "notifications", notificationId);

    try {
      // optional: read the doc first to check recipient (client-side guard)
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        if (data?.to && data.to !== user.uid) {
          throw new Error("Not the notification recipient");
        }
      }

      // Important: update ONLY the `read` boolean to satisfy your rules
      await updateDoc(ref, { read: true, readAt: serverTimestamp() });
      // console.log("✅ Notification marked as read:", notificationId);
    } catch (err) {
      console.error("🔥 Failed to update notification:", err);
      throw err;
    }
  }
  async function persistNotificationReadViaFunction(notificationId) {
    try {
      await markNotificationReadDirectly(notificationId);
    } catch (err) {
      console.error("Failed to mark notification read directly:", err);
    }
  }

  async function markAsReadAndOpen(n) {
    if (!n) {
      setNotifOpen(false);
      return;
    }

    optimisticMarkLocalRead(n.id);

    try {
      if (n.id) await persistNotificationReadViaFunction(n.id);
    } catch (err) {
      console.error("persistNotificationReadViaFunction failed:", err);
      revertLocalRead(n.id);
      if (String(err).includes("not-signed-in")) return; // login was prompted
    } finally {
      try {
        if (n.url) {
          const w = window.open(n.url, "_blank", "noopener,noreferrer");
          if (w) w.opener = null;
        }
      } catch (openErr) {
        console.error("Failed to open notification URL:", openErr);
      }
      setNotifOpen(false);
    }
  }

  async function handleMarkAllRead() {
    const ids = (Array.isArray(localNotifications) ? localNotifications : []).filter(n => !n.read).map(n => n.id);
    if (!ids.length) return;

    // optimistic UI
    setLocalNotifications(prev => Array.isArray(prev) ? prev.map(x => ({ ...x, read: true })) : prev);

    if (!currentUser || !currentUser.uid) {
      handleOpenAuthClick("login");
      // revert optimistic
      setLocalNotifications(prev => Array.isArray(prev) ? prev.map(x => ({ ...x, read: false })) : prev);
      return;
    }

    try {
      const db = getFirestore(firebaseApp);
      const batch = writeBatch(db);

      ids.forEach(id => {
        const ref = doc(db, "notifications", id);
        batch.update(ref, { read: true });
      });

      await batch.commit();
      return;
    } catch (err) {
      console.error("Batch markAllRead failed:", err);
      // revert optimistic
      setLocalNotifications(prev => Array.isArray(prev) ? prev.map(x => ({ ...x, read: false })) : prev);
    }
  }


  function formatDate(ts) {
    if (!ts) return "";
    try {
      const date = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
      // date-fns returns strings like "about 9 hours ago" or "over 1 year ago"
      const raw = formatDistanceToNow(date, { addSuffix: true });
      // strip leading qualifiers like "about " or "over "
      return raw.replace(/^(about|over)\s+/i, "");
    } catch {
      return "";
    }
  }


  // ---------- Render ----------
  return (
    <>
      <nav className="navbar" style={{ zIndex: 1000 }}>
        <div className="navbar-left">
          <button className="logo-button" onClick={handleLogoClick} type="button" aria-label="Go to home">
            <span className="logo">Jift</span>
          </button>
        </div>

        <div className="navbar-right" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={toggleTheme} className="theme-toggle-btn" type="button" aria-label="Toggle theme">
            {theme === "light" ? "🌙" : "☀️"}
          </button>

          {currentUser && (
            <>
              {/* Message icon */}
              <div className="icon-wrapper" ref={msgRef} style={{ position: "relative" }}>
                <button
                  className="icon-btn"
                  onClick={handleToggleMsg}
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={msgOpen}
                  aria-label="Open messages"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                    <path d="M21 6.5A2.5 2.5 0 0 0 18.5 4H5.5A2.5 2.5 0 0 0 3 6.5v7A2.5 2.5 0 0 0 5.5 16H7v3l3-3h8.5A2.5 2.5 0 0 0 21 13.5v-7z" />
                  </svg>
                  {unreadMessagesCount > 0 && <span className="icon-dot" />}
                </button>

                {msgOpen && (
                  <div className="notif-dropdown messages-dropdown" role="menu" aria-label="Messages">
                    <div className="notif-dropdown-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px" }}>
                      <strong>Messages</strong>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {unreadMessagesCount > 0 && <span className="messages-unread-count" aria-hidden="true">{unreadMessagesCount}</span>}
                        <button onClick={openAllMessages} type="button" className="notif-action" aria-label="View all messages">View all</button>
                        <button onClick={() => setMsgOpen(false)} type="button" className="notif-action">Close</button>
                      </div>
                    </div>

                    <div className="notif-list messages-list" style={{ maxHeight: 320, overflowY: "auto" }}>
                      {convLoading && <div className="muted" style={{ padding: 12 }}>Loading…</div>}
                      {!convLoading && conversationsArray.length === 0 && <div className="muted" style={{ padding: 12 }}>No conversations.</div>}

                      {conversationsArray.map((conv) => {
                        const partnerUid = conv.partnerUid || (conv.participants && conv.participants.find(p => p !== currentUid)) || `unknown-${conv.id}`;
                        const profileObj = conv.partnerProfile || conv._raw?.partnerProfile || null;
                        const name = profileObj?.displayName || profileObj?.handle || partnerUid;
                        const avatar = profileObj?.photoURL || profileObj?.avatarUrl || null;
                        const preview = conv.preview || conv.lastMessage?.text || "";
                        const ts = conv.ts || conv.lastUpdated || conv.lastMessage?.createdAt || null;
                        let unreadForMe = 0;
                        if (typeof conv.unreadCount === "number") {
                          unreadForMe = conv.unreadCount;
                        } else if (Array.isArray(conv.items)) {
                          unreadForMe = conv.items.filter((it) => {
                            const isRead = typeof it.read === "boolean" ? it.read : false;
                            if (it.to && currentUid) return !isRead && it.to === currentUid;
                            return !isRead;
                          }).length;
                        }

                        return (
                          <button
                            key={conv.id}
                            className={`message-item ${unreadForMe > 0 ? "unread" : "read"}`}
                            onClick={() => openMessagesForPartner(partnerUid)}
                            type="button"
                            aria-label={`${unreadForMe > 0 ? `${unreadForMe} unread` : ""} conversation with ${name}`}
                          >
                            <div className="message-left">
                              <div className={`avatar ${unreadForMe > 0 ? "avatar-unread" : ""}`} aria-hidden="true">
                                {avatar ? <img src={avatar} alt="" /> : (name ? name.charAt(0).toUpperCase() : "U")}
                              </div>
                            </div>

                            <div className="message-body">
                              <div className="message-row">
                                <div className="message-title">{name}</div>
                                <div className="message-time">{formatDate(ts)}</div>
                              </div>

                              <div className="message-preview truncate">{preview}</div>
                            </div>

                            <div className="message-right" aria-hidden="true">
                              {unreadForMe > 0 ? <span className="message-badge">{unreadForMe > 9 ? "9+" : unreadForMe}</span> : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Notifications icon */}
              <div className="icon-wrapper" ref={notifRef} style={{ position: "relative" }}>
                <button
                  className="icon-btn"
                  onClick={handleToggleNotif}
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={notifOpen}
                  aria-label={unreadCount > 0 ? `You have ${unreadCount} unread notifications` : "Open notifications"}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                    <path d="M12 2a6 6 0 0 0-6 6v4.586L4.293 15.293A1 1 0 0 0 5 17h14a1 1 0 0 0 .707-1.707L18 12.586V8a6 6 0 0 0-6-6zM8 20a4 4 0 0 0 8 0H8z" />
                  </svg>
                  {unreadCount > 0 && <span className="icon-badge">{unreadCount}</span>}
                </button>

                {notifOpen && (
                  <div className="notif-dropdown" role="menu" aria-label="Notifications">
                    <div className="notif-dropdown-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px" }}>
                      <strong>Notifications</strong>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={handleMarkAllRead} type="button" className="notif-action">Mark all read</button>
                        <button onClick={() => setNotifOpen(false)} type="button" className="notif-action">Close</button>
                      </div>
                    </div>

                    <ul className="notif-list" role="list">
                      {notifLoading && <div className="notif-empty">Loading…</div>}
                      {!notifLoading && (!Array.isArray(localNotifications) || localNotifications.length === 0) && (
                        <div className="notif-empty">No notifications.</div>
                      )}
                      {(Array.isArray(localNotifications) ? localNotifications : []).map((n) => {
                        const isRead = !!n.read;
                        const senderUid = n.from;
                        const sender = notifSenders?.[senderUid];
                        const handle = sender?.handle ? `@${sender.handle}` : sender?.displayName || senderUid;
                        const avatar = sender?.photoURL || n.avatarUrl || null;
                        const time = formatDate(n.createdAt);

                        return (
                          <li key={n.id}>
                            <button
                              type="button"
                              className={`notif-item ${isRead ? "read" : "unread"}`}
                              onClick={(e) => {
                                e.preventDefault();
                                // extra guard/diagnostics
                                if (!n || !n.id) {
                                  console.warn("[Navbar] notification missing id:", n);
                                  return;
                                }
                                // call your existing function
                                markAsReadAndOpen(n);
                              }}
                            >
                              {/* left side avatar */}
                              <div className={`notif-avatar ${!isRead ? "unread" : ""}`}>
                                {avatar ? (
                                  <img src={avatar} alt={handle} />
                                ) : (
                                  handle?.charAt(0)?.toUpperCase() || "U"
                                )}
                              </div>

                              {/* right side text */}
                              <div className="notif-body">
                                {/* row 1: handle + time */}
                                <div className="notif-row">
                                  <div className="title" title={handle}>{handle}</div>
                                  <div className="notif-time">{time}</div>
                                </div>

                                {/* row 2: message/title */}
                                <div className="notif-preview">{n.title || n.text || ""}</div>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}

          {currentUser ? (
            <div className="user-menu" ref={menuRef}>
              <button className="user-button" onClick={handleToggleMenu} aria-haspopup="true" aria-expanded={open} aria-label="Open user menu" type="button">
                Hi, {displayName}
              </button>

              {open && (
                <ul className="user-dropdown" role="menu">
                  <li role="none">
                    <button role="menuitem" className="dropdown-item" onClick={() => { setOpen(false); setShowProfileModal(true); }} type="button">👤 Me</button>
                  </li>
                  <li role="none">
                    <button role="menuitem" className="dropdown-item" onClick={handleViewTrips} type="button">📁 My Trips</button>
                  </li>
                  <li role="none">
                    <button role="menuitem" className="dropdown-item" onClick={handleAddTrip} type="button">➕ Add Trip</button>
                  </li>
                  <li role="none">
                    <button role="menuitem" className="dropdown-item" onClick={handleLogout} type="button">🚪 Logout</button>
                  </li>
                </ul>
              )}
            </div>
          ) : (
            <button onClick={() => handleOpenAuthClick("login")} className="theme-btn primary" type="button">Login / Signup</button>
          )}
        </div>
      </nav>

      {showProfileModal && (
        <MeModal open={showProfileModal} onClose={() => setShowProfileModal(false)} profile={localProfile} currentUser={currentUser} onSave={handleSaveProfileToFirestore} />
      )}

      <MessagesInboxModal
        open={showMessagesModal}
        onClose={() => { setShowMessagesModal(false); setMessagesModalPartner(null); }}
        db={db}
        auth={auth}
        user={currentUser}
        useFirebase={true}
        initialPartner={messagesModalPartner}
      />
    </>
  );
}
